import { assertUnreachable, isDefined, isNotDefined } from '@sgme/fp';
import { HStack, Tooltip } from '@sgme/ui';
import { clsx } from 'clsx';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { optionApi, useCancelOptionRfsMutation } from '@/api/option/index.ts';
import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { TimeProgress } from '@/App/tiles/shared/TimeProgress.tsx';
import { SgButton } from '@/components/sg-design-system/components/input/SgButton/index.tsx';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { getQuote, getQuoteType, getRfsId, selectRfsStatus } from '@/store/option/selectors/optionSelectors.ts';
import type { FxOption } from '@/store/option/state.ts';
import { requestOptionsRfsThunk } from '@/store/option/thunks/thunk.ts';

export function PriceButton({ tileId }: { tileId: string }) {
  const rfsStatus = useOptionSelector((option) => selectRfsStatus(option));

  switch (rfsStatus) {
    case 'NOT_PRICEABLE':
      return <NotPriceable pricingType="Indicative" />;

    case 'STREAMING_EXECUTABLE':
      return <Progress tileId={tileId} />;

    case 'AWAITING_INDICATIVE':
      return <ShortcutExecutable tileId={tileId} />;

    case 'AWAITING_EXECUTABLE':
      return <AwaitingExecutable tileId={tileId} />;

    case 'PRICEABLE':
      return <Priceable tileId={tileId} pricingType="Indicative" forceTradeable={false} />;
    case 'ERROR':
      return <Priceable tileId={tileId} pricingType="Indicative" forceTradeable={false} />;
    case 'STREAMING_INDICATIVE':
      return <Priceable tileId={tileId} pricingType="Tradeable" forceTradeable={true} />;

    case 'BYPASSABLE_ERROR':
      return <Bypassable tileId={tileId} />;
    default:
      assertUnreachable(rfsStatus, `Unhandled price stream status: ${rfsStatus}`);
  }
}

function AwaitingExecutable({ tileId }: { tileId: string }) {
  const [cancelOptionRfs] = useCancelOptionRfsMutation();

  const rfsId = useOptionSelector((option) => getRfsId(option));
  const handleCancelRequest = useCallback(() => {
    if (isDefined(rfsId)) {
      cancelOptionRfs({
        tileId,
        rfsId,
      });
    }
  }, [rfsId, tileId, cancelOptionRfs]);

  return (
    <div className="progress" style={{ height: '20px' }}>
      <div className="progress-bar progress-bar-striped progress-bar-animated bg-info w-100" role="progressbar" />
      <button
        type="button"
        className="btn btn-sm btn-flat-primary"
        data-e2e="cancel-rfs-button"
        onClick={handleCancelRequest}
      >
        <i className="icon">close</i>
      </button>
    </div>
  );
}

function Progress({ tileId }: { tileId: string }) {
  const [cancelOptionRfs] = useCancelOptionRfsMutation();

  const { rfsId, remainingTime, totalTime } = useOptionSelector((option) => selectProgressData(option));
  const handleCancelRequest = useCallback(() => {
    if (isDefined(rfsId)) {
      cancelOptionRfs({
        tileId,
        rfsId,
      });
    }
  }, [rfsId, tileId, cancelOptionRfs]);

  return (
    <TimeProgress totalTime={totalTime} remainingTime={remainingTime} onCancel={handleCancelRequest} data-e2e="rfs" />
  );
}

function Priceable({
  tileId,
  pricingType,
  forceTradeable,
}: {
  tileId: string;
  pricingType: 'Tradeable' | 'Indicative';
  forceTradeable: boolean;
}) {
  const dispatch = useAppDispatch();
  const rfsId = useOptionSelector((option) => getRfsId(option));

  const skippedCreditCheck = useAppSelector((state) => {
    const rfsResponse = optionApi.endpoints.requestOptionRfs.select({ requestId: rfsId, fixedCacheKey: rfsId })(state);
    return rfsResponse.isSuccess && rfsResponse.data.skipCreditCheck;
  });
  return (
    <button
      data-e2e={`price-button-${pricingType.toLowerCase()}`}
      tabIndex={-1}
      type="button"
      className={clsx('btn btn-info btn-sm btn-block', { 'btn-warning': skippedCreditCheck })}
      onClick={() => {
        dispatch(requestOptionsRfsThunk({ tileId, forceTradeable, skipCreditCheck: skippedCreditCheck }));
      }}
    >
      <FormattedMessage id={`request.${pricingType}.price`} />
    </button>
  );
}

function NotPriceable({ pricingType }: { pricingType: 'Tradeable' | 'Indicative' }) {
  return (
    <button
      data-e2e={`price-button-${pricingType.toLowerCase()}`}
      tabIndex={-1}
      type="button"
      className="btn btn-info btn-sm btn-block"
      disabled={true}
    >
      <FormattedMessage id={`request.${pricingType}.price`} />
    </button>
  );
}

function Bypassable({ tileId }: { tileId: string }) {
  const dispatch = useAppDispatch();
  const quoteType = useOptionSelector((option) => getQuoteType(option));
  const bypassCheck = true;
  const forceTradeable = quoteType === 'Indicative' || quoteType === 'Withdraw';

  function bypass() {
    dispatch(requestOptionsRfsThunk({ tileId, forceTradeable: false, skipCreditCheck: true }));
  }
  function skipToExecutable() {
    dispatch(requestOptionsRfsThunk({ tileId, forceTradeable, skipCreditCheck: true }));
  }

  return (
    <HStack fullWidth>
      <button onClick={bypass} type="button" className="btn btn-warning btn-sm btn-block" data-e2e={'button-price'}>
        <FormattedMessage
          id={
            quoteType === 'Indicative'
              ? 'option.pricingButton.bypass.indicative.label'
              : 'option.pricingButton.bypass.executable.label'
          }
        />
      </button>
      <Tooltip contents={<FormattedMessage id="option.pricingButton.shortcutExecutable.tooltip" />}>
        <button
          type="button"
          className={`btn btn-sm btn-icon btn-flat-${bypassCheck ? 'warning' : 'info'}`}
          onClick={skipToExecutable}
          data-e2e={'button-skip-to-executable-price'}
        >
          <i className="icon">skip_next</i>
        </button>
      </Tooltip>
    </HStack>
  );
}

function ShortcutExecutable({ tileId }: { tileId: string }) {
  const dispatch = useAppDispatch();
  const rfsId = useOptionSelector((option) => getRfsId(option));
  const [cancelOptionRfs] = useCancelOptionRfsMutation();

  const bypassCheck = false;

  const handleCancelRequest = useCallback(() => {
    if (isNotDefined(rfsId)) {
      return;
    }
    cancelOptionRfs({
      tileId,
      rfsId,
    });
  }, [tileId, cancelOptionRfs, rfsId]);

  return (
    <div className="progress" style={{ height: '20px' }}>
      <div className="progress-bar progress-bar-striped progress-bar-animated bg-info w-100" role="progressbar" />
      <SgButton
        type="button"
        purpose="discreet"
        size="sm"
        className="btn btn-sm btn-flat-primary flex-grow-0"
        onClick={handleCancelRequest}
      >
        <i className="icon icon-sm">close</i>
      </SgButton>
      <Tooltip contents={<FormattedMessage id="option.pricingButton.shortcutExecutable.tooltip" />}>
        <button
          type="button"
          className={`btn btn-sm btn-icon btn-flat-${bypassCheck ? 'warning' : 'info'}`}
          onClick={() => {
            dispatch(requestOptionsRfsThunk({ tileId, forceTradeable: true }));
          }}
          data-e2e={'skip-to-executable-price-button'}
        >
          <i className="icon">skip_next</i>
        </button>
      </Tooltip>
    </div>
  );
}

function selectProgressData(option: FxOption) {
  const quote = getQuote(option);
  const totalTime = option.properties.rfs.status === 'PRICING' ? option.properties.rfs.initialRfsWindow : undefined;

  return {
    rfsId: getRfsId(option),
    remainingTime: quote?.rfsWindow,
    totalTime,
  };
}
